import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from 'context/user-context';
import { GET_INSTALLATION_FEATURE_SETTINGS, GET_PROTECTION_SETTINGS, GET_ORDER_EDITING_WORKSPACE } from 'gql/queries';
import { UPDATE_PROTECTION_SETTINGS, UPDATE_STORE, SAVE_RETURN_WORKFLOW_SETTINGS,
  SAVE_TRACKING_BILLING_SETTINGS, SAVE_ORDER_EDITING_SETTINGS } from 'gql/mutations';
import type { InlineFeature, InstallationFeatureChange, InstallationFeatureSnapshot } from 'types/installationFeatureSettings';
import { editingAvailable, featureChoice, featureObject, prepareGuaranteeInput, readEditingConfiguration,
  readFeatureStore, readGuaranteePricing } from 'utils/installationFeatureSettings';

const empty = (): InstallationFeatureSnapshot => ({ store: null, pricing: null, editing: null });
const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);
const label: Record<InlineFeature, string> = {
  guarantee: 'Guarantee', gifts: 'Gifts', returns: 'Returns', tracking: 'Tracking', orderEditing: 'Order editing',
};
const guaranteeSubscriptions = (snapshot: InstallationFeatureSnapshot) =>
  featureObject(snapshot.store?.features)?.SUPPORT_SUBSCRIPTIONS;
const guaranteeWritable = (snapshot: InstallationFeatureSnapshot) => !!snapshot.store && !!snapshot.pricing
  && (guaranteeSubscriptions(snapshot) === undefined || guaranteeSubscriptions(snapshot) === false);
const relevant = (snapshot: InstallationFeatureSnapshot, feature: InlineFeature) => {
  if (feature === 'guarantee') return { pricing: snapshot.pricing, currency: snapshot.store?.currency,
    supportSubscriptions: guaranteeSubscriptions(snapshot) };
  if (feature === 'orderEditing') return snapshot.editing;
  if (feature === 'gifts') return snapshot.store?.freeGifts;
  if (feature === 'returns') return snapshot.store?.policySettings;
  return snapshot.store?.features;
};

export function useInstallationFeatureSettings(storeId: string, domain: string, canManage: boolean, blocked: boolean) {
  const client = useApolloClient();
  const { user } = useUser();
  const scope = JSON.stringify([storeId, domain.trim().toLowerCase(), user?.id ?? null, canManage]);
  const activeScope = useRef(scope); activeScope.current = scope;
  const gate = useRef({ canManage, blocked }); gate.current = { canManage, blocked };
  const generation = useRef(0), lock = useRef(false);
  const [state, setState] = useState({ scope, snapshot: empty(), loading: true, busy: false,
    needsRefresh: false, error: '', notice: '', resetVersion: 0,
    lastSaved: null as { feature: InlineFeature; sequence: number } | null });
  const visible = state.scope === scope ? state : { ...state, scope, snapshot: empty(), loading: true,
    busy: false, needsRefresh: false, error: '', notice: '', lastSaved: null };

  const fetchSnapshot = useCallback(async () => {
    const options = { fetchPolicy: 'no-cache' as const, context: { queryDeduplication: false } };
    const reads = await Promise.allSettled([
      Promise.resolve().then(() => client.query({ ...options, query: GET_INSTALLATION_FEATURE_SETTINGS, variables: { storeId } })),
      Promise.resolve().then(() => client.query({ ...options, query: GET_PROTECTION_SETTINGS, variables: { storeId } })),
      canManage ? Promise.resolve().then(() => client.query({ ...options, query: GET_ORDER_EDITING_WORKSPACE, variables: { input: { storeId } } }))
        : Promise.resolve({ data: { orderEditingWorkspace: null } }),
    ]);
    const value = (index: number, field: string) => {
      const result = reads[index];
      return result.status === 'fulfilled' ? result.value.data?.[field] : null;
    };
    return { snapshot: { store: readFeatureStore(value(0, 'store'), storeId, domain),
      pricing: readGuaranteePricing(value(1, 'protectionSettings'), storeId),
      editing: readEditingConfiguration(value(2, 'orderEditingWorkspace'), storeId) },
    failed: reads.some(result => result.status === 'rejected') };
  }, [client, storeId, domain, canManage]);

  const refresh = useCallback(async () => {
    if (lock.current || !user?.id) return;
    lock.current = true;
    const request = ++generation.current;
    const current = () => activeScope.current === scope && generation.current === request;
    setState(previous => ({ ...previous, scope, loading: true, busy: true, notice: '', error: '' }));
    try {
      const result = await fetchSnapshot();
      if (!current()) return;
      setState(previous => ({ ...previous, scope, snapshot: result.snapshot, resetVersion: previous.resetVersion + 1,
        needsRefresh: result.failed || !result.snapshot.store,
        error: result.failed || !result.snapshot.store ? 'Feature settings could not be loaded. Reload to check.' : '',
        lastSaved: null }));
    } finally {
      if (current()) { lock.current = false; setState(previous => ({ ...previous, loading: false, busy: false })); }
    }
  }, [fetchSnapshot, scope, user?.id]);

  useEffect(() => {
    const scopeGeneration = generation;
    generation.current++;
    lock.current = false;
    setState(previous => ({ ...previous, scope, snapshot: empty(), error: '', notice: '',
      needsRefresh: false, loading: true, busy: false, lastSaved: null }));
    void refresh();
    return () => { scopeGeneration.current++; };
  }, [scope, refresh]);

  const save = async (change: InstallationFeatureChange): Promise<boolean> => {
    const baseline = visible.snapshot;
    if (lock.current || !gate.current.canManage || gate.current.blocked || visible.needsRefresh
      || visible.loading || !baseline.store) return false;
    let variables: Record<string, unknown>, mutation;
    if (change.feature === 'guarantee') {
      const input = prepareGuaranteeInput(baseline.pricing, change.amount);
      if (!input || !guaranteeWritable(baseline)) return false;
      variables = { input }; mutation = UPDATE_PROTECTION_SETTINGS;
    } else if (change.feature === 'orderEditing') {
      const config = baseline.editing;
      if (!config || !editingAvailable(config, 'address') || !editingAvailable(config, 'cancellation')) return false;
      variables = { input: { storeId, revision: config.revision,
        settings: { ...config.settings, editShipping: change.editShipping, cancelOrder: change.cancelOrder } } };
      mutation = SAVE_ORDER_EDITING_SETTINGS;
    } else {
      if (featureChoice(change.feature, baseline.store) === null) return false;
      if (change.feature === 'gifts') {
        variables = { storeId, set: { freeGifts: { ...featureObject(baseline.store.freeGifts), enabled: change.enabled } } };
        mutation = UPDATE_STORE;
      } else if (change.feature === 'returns') {
        variables = { input: { storeId, returnsEnabled: change.enabled } }; mutation = SAVE_RETURN_WORKFLOW_SETTINGS;
      } else {
        if (change.enabled && !change.feeAccepted) return false;
        variables = { input: { storeId, enabled: change.enabled } }; mutation = SAVE_TRACKING_BILLING_SETTINGS;
      }
    }
    lock.current = true;
    const request = ++generation.current;
    const current = () => activeScope.current === scope && generation.current === request;
    setState(previous => ({ ...previous, busy: true, error: '', notice: '' }));
    try {
      const preflight = await fetchSnapshot();
      if (!current()) return false;
      if (!preflight.snapshot.store || !same(relevant(preflight.snapshot, change.feature), relevant(baseline, change.feature))
        || (change.feature === 'guarantee' && !guaranteeWritable(preflight.snapshot))) {
        setState(previous => ({ ...previous, needsRefresh: true,
          error: 'Feature settings changed since you opened them. Reload before saving; your edits are preserved.' }));
        return false;
      }
      if (!gate.current.canManage || gate.current.blocked) return false;
      const response = await client.mutate({ mutation, variables });
      if (!current()) return false;
      const input = variables.input as Record<string, unknown> | undefined;
      let acknowledged = false;
      if (change.feature === 'guarantee') acknowledged = response.data?.setProtectionSettings === true;
      else if (change.feature === 'gifts') acknowledged = response.data?.updateStore?.id === storeId;
      else if (change.feature === 'returns') {
        const returns = featureObject(featureObject(response.data?.saveReturnWorkflowSettings?.policySettings)?.returns);
        acknowledged = returns?.enabled === change.enabled;
      } else if (change.feature === 'tracking') {
        const result = featureObject(response.data?.saveTrackingBillingSettings);
        acknowledged = result?.storeId === storeId && result.enabled === change.enabled
          && featureChoice('tracking', { ...baseline.store, features: result.features }) === change.enabled;
      } else {
        const result = featureObject(response.data?.saveOrderEditingSettings);
        acknowledged = result?.storeId === storeId && result.revision === baseline.editing!.revision + 1
          && same(result.settings, input?.settings);
      }
      if (!acknowledged) throw new Error('Unconfirmed configuration update');
      const readback = await fetchSnapshot();
      if (!current()) return false;
      if (!readback.snapshot.store) throw new Error('Unconfirmed store');
      let confirmed = false;
      if (change.feature === 'guarantee') {
        const record = readback.snapshot.pricing;
        confirmed = !!record && Object.entries(input!).every(([key, value]) =>
          same(key === 'type' ? record.protectionType : record[key as keyof typeof record], value));
      } else if (change.feature === 'orderEditing') {
        confirmed = readback.snapshot.editing?.revision === baseline.editing!.revision + 1
          && same(readback.snapshot.editing?.settings, input?.settings);
      } else confirmed = featureChoice(change.feature, readback.snapshot.store) === change.enabled
        && (change.feature !== 'gifts' || same(readback.snapshot.store.freeGifts, (variables.set as any).freeGifts));
      if (!confirmed) throw new Error('Unconfirmed configuration readback');
      setState(previous => {
        const snapshot = { ...previous.snapshot };
        if (change.feature === 'guarantee') snapshot.pricing = readback.snapshot.pricing;
        else if (change.feature === 'orderEditing') snapshot.editing = readback.snapshot.editing;
        else snapshot.store = { ...previous.snapshot.store!, ...(change.feature === 'gifts'
          ? { freeGifts: readback.snapshot.store!.freeGifts } : change.feature === 'returns'
            ? { policySettings: readback.snapshot.store!.policySettings } : { features: readback.snapshot.store!.features }) };
        return { ...previous, snapshot, notice: `${label[change.feature]} settings saved.`,
          lastSaved: { feature: change.feature, sequence: request } };
      });
      return true;
    } catch {
      if (current()) setState(previous => ({ ...previous, needsRefresh: true,
        error: 'The update could not be confirmed. Reload settings before trying again; your edits are preserved.' }));
      return false;
    } finally {
      if (current()) { lock.current = false; setState(previous => ({ ...previous, busy: false })); }
    }
  };
  return { ...visible, refresh, save };
}
